import "../styles/index.scss";

import * as THREE from "three";
import GLTFLoader from "three-gltf-loader";
import OrbitControls from "three-orbitcontrols";
const AColorPicker = require("a-color-picker");

import {
  settings as defaults
} from "./settings.js";

import {
  stringArraytoArray,
  getMaterial,
  stringToNode,
  round
} from "./helpers";

import {
  slide
} from "./slider.js";

// Refactor

class Merch3d {
  constructor(settings = {}) {
    this.settings = Object.assign(defaults, settings);

    // UI
    this.uiParentDom; // Dom element created in BuildUI
    this.uiLoadingAnimationDom; // Dom element created in BuildUI
    this.uiSwatchParentDom; //  Dom element for swatch list
    this.uiDragNoticeDom; // Dom element created in BuildUI
    this.uiSectionTitle; // Title inside the Section Selector
    this.uiSectionPrev; // Previous button
    this.uiSectionNext; // Next button

    // Scene
    this.scene;
    this.renderer;
    this.camera;
    this.raycaster;
    this.controls;

    // Model
    this.model; // The actual models scene (inside the GLTF file)
    this.currentModelUrl; // A URL of the current model
    this.sectionStore = []; // This keeps track of each section and its material;

    // Other
    this.swatches;
    this.activeSections = [];
    this.activeOption;
    this.flashMaterial;
    this.loaded = false; // This lets the model know when to stop rotating
    this.initRotate = 0;
    this.userMouseClick = {
      // This is used to determine whether the user is dragging or not
      down: {
        x: null,
        y: null
      },
      up: {
        x: null,
        y: null
      }
    };
  }

  /**
   * Public method is used to initiate the class
   * this requires a model to be present
   *
   */
  run() {
    if (this.settings.models) {
      this._init();
    } else {
      console.warn("Merch3d won't start without a model path.");
    }
  }

  debug() {
    console.group();
    console.log(
      this.uiParentDom,
      this.uiLoadingAnimationDom,
      this.uiSwatchParentDom,
      this.uiDragNoticeDom,
      this.uiSectionTitle,
      this.uiSectionPrev,
      this.uiSectionNext,

      // Scene
      this.scene,
      this.renderer,
      this.camera,
      this.raycaster,
      this.controls,

      // Model
      this.model,
      this.currentModelUrl,
      this.sectionStore,

      // Other
      this.swatches,
      this.activeSections,
      this.activeOption,
      this.flashMaterial,
      this.loaded,
      this.initRotate,
      this.userMouseClick
    );
    console.groupEnd();
  }

  _init() {
    this._buildUi();
    this._buildScene();
    this._animate();
    this._loadModel(0); // 0 is the index of this.settings.models
    this._addEventListeners();
  }

  /**
   * Build UI
   *
   * The app relies on certain DOM elements to function
   * This builds two main elements
   * The container element .m3d-container
   * the tray element .m3d-tray-container
   *
   * It also builds a loader graphic and the drag notice
   */
  _buildUi() {
    /**
     * Parent container is the DOM element that the canvas
     * will render inside. the class or id name is set in
     * the settings, if it doesn't exist in the document,
     * it is automatically created with user set or default
     * selector .m3d-container
     */
    if (!document.querySelector(this.settings.uiParent)) {
      let parent = stringToNode(this.settings.uiParent, ".m3d-container");

      this.uiParentDom = parent;
      document.body.appendChild(this.uiParentDom);
    } else {
      this.uiParentDom = document.querySelector(this.settings.uiParent);
    }

    this.uiParentDom.style.position = "relative";

    /**
     * Selector component,
     * This selects different parts of the model
     * using the left and right arrows
     *
     */

    const uiSectionSelector = `<div class="m3d-model-sections" style="position: absolute; left: 1rem; bottom: 4rem">
      <span class="m3d-model-sections__title"></span>
      <ul class="m3d-model-sections__list"></ul>
      <div class="m3d-model-sections__arrows">
        <button class="m3d-model-sections__prev" id="js-toggle-prev" data-option="prev">
        <img src="../../public/assets/arrow.svg"
            alt="Next section"></button>
        <button class="m3d-model-sections__next" id="js-toggle-next" data-option="next">
        <img src="../../public/assets/arrow.svg"
            alt="Previous ection"></button>
      </div>
    </div>`;

    /**
     * Loader Graphic
     * This graphic is CSS animation that overlays
     *  the canvas while a model or texture is loading
     *
     */

    const loaderGraphic = `
    <div class="m3d-loader">
      <div class="${this.settings.uiLoadingAnimation.substring(1)}"></div>
    </div>
   `;

    /**
     * Drag Notice
     * This is a badge that shows on load to prompt
     * the user that the model can be dragged around
     * This can be disabled in the settings
     */
    const dragNotice = `
    <span class="m3d-drag-notice" style="display: ${
      this.settings.showDragNotice ? "flex" : "none"
    }">
      Drag to rotate 360&#176;
    </span>
   `;

    this.uiParentDom.innerHTML +=
      uiSectionSelector + loaderGraphic + dragNotice;

    this.uiLoadingAnimationDom = document.querySelector(
      this.settings.uiLoadingAnimation
    );
    this.uiDragNoticeDom = document.querySelector(this.settings.uiDragNotice);

    this.uiSectionTitle = document.querySelector(".m3d-model-sections__title");
    this.uiSectionPrev = document.querySelector(".m3d-model-sections__prev");
    this.uiSectionNext = document.querySelector(".m3d-model-sections__next");

    /**
     * Fill the Section Selector
     * Fill the swatches tray with a list of swatches
     *
     * More information above the methods below
     */

    this._fillSectionSelector(0); // 0 is the index of this.settings.models
    this._buildSwatches(this.settings.colors);
  }

  /**
   * Build scene:
   * - Scene
   * - Renderer
   * - Camera
   * - Lights
   * - Raycaster
   * - Floor
   * - Controls
   */
  _buildScene() {
    /**
     * Scene
     *
     * Initialize a new Threejs Scene
     */
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(
      parseInt("0x" + this.settings.environmentColor)
    );
    this.scene.fog = new THREE.Fog(
      parseInt("0x" + this.settings.environmentColor),
      20,
      100
    );

    /**
     * Renderer
     *
     * Initialize a new Threejs Renderer
     *
     * PreserveDrawingBuffer allows the app
     * to convert the scenec to a PNG
     * which is not possible without it
     */
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      preserveDrawingBuffer: true
    });
    this.renderer.shadowMap.enabled = true;
    this.renderer.setPixelRatio(window.devicePixelRatio);

    // Add the renderer into the Merch3d parent dom
    this.uiParentDom.appendChild(this.renderer.domElement);

    /**
     * Camera
     *
     * Initialize a new Threejs Camera
     *
     */
    this.camera = new THREE.PerspectiveCamera(
      this.settings.cameraFOV,
      this.renderer.domElement.clientWidth /
      this.renderer.domElement.clientHeight,
      0.1,
      1000
    );
    this.camera.position.z = this.settings.cameraDistance;
    this.camera.position.x = 0;

    /**
     * Lights
     *
     * Initialize the new Threejs lights
     *
     * - 2x Directional lights
     * - 1x Hemisphere light
     *
     * The directional lights mirror each other,
     * one of them doesn't cast shadows, and is
     * dimmer than the other
     */
    const hemiLight = new THREE.HemisphereLight(
      parseInt("0x" + this.settings.hemisphereLightColor),
      parseInt("0x" + this.settings.hemisphereLightColor),
      this.settings.hemisphereLightStrength
    );
    hemiLight.position.set(0, 50, 0);
    this.scene.add(hemiLight);

    // First directional light
    const dirLight = new THREE.DirectionalLight(
      parseInt("0x" + this.settings.directionalLightColor),
      this.settings.directionalLightStrength
    );
    dirLight.position.set(
      parseFloat(this.settings.directionalLightPosition.x),
      parseFloat(this.settings.directionalLightPosition.y),
      parseFloat(this.settings.directionalLightPosition.z)
    );
    dirLight.castShadow = true;
    dirLight.shadow.mapSize = new THREE.Vector2(
      this.settings.shadowResolution,
      this.settings.shadowResolution
    );

    this.scene.add(dirLight);

    // Mirrored directional light

    const m_dirLight = new THREE.DirectionalLight(
      parseInt("0x" + this.settings.directionalLightColor),
      this.settings.directionalLightStrength * 0.5
    );
    m_dirLight.position.set(
      parseFloat(this.settings.directionalLightPosition.x) * -1,
      parseFloat(this.settings.directionalLightPosition.y),
      parseFloat(this.settings.directionalLightPosition.z) * -1
    );

    this.scene.add(m_dirLight);

    // Mirrored directional light

    const m_dirLight2 = new THREE.DirectionalLight(
      parseInt("0x" + 'ffffff'),
      this.settings.directionalLightStrength * 0.5
    );
    m_dirLight2.position.set(
      parseFloat(this.settings.directionalLightPosition.x) * -0.5,
      1,
      parseFloat(this.settings.directionalLightPosition.z) * -0.5
    );

    this.scene.add(m_dirLight2);

    /**
     * Rayaster
     *
     * Initialize a new Threejs Raycaster
     *
     * This is used when clicking on the model
     * to select different sections
     */
    this.raycaster = new THREE.Raycaster();

    /**
     * Floor
     *
     * Initialize a new Threejs Plane
     *
     * The color is manually dialed in to match the background color
     */
    let floorGeometry = new THREE.PlaneGeometry(5000, 5000, 50, 50);

    let floorMaterial = getMaterial({
      color: this.settings.floorColor,
      shininess: 0
    });

    let floor = new THREE.Mesh(floorGeometry, floorMaterial);
    floor.rotation.x = -0.5 * Math.PI;
    floor.receiveShadow = true;
    floor.position.y = this.settings.offsetHeight;
    this.scene.add(floor);

    /**
     * Controls
     *
     * Initialize Orbit Controls
     */
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);

    this.controls.maxPolarAngle = THREE.Math.degToRad(
      parseFloat(this.settings.maxYRotate)
    );

    this.controls.minPolarAngle = THREE.Math.degToRad(
      parseFloat(this.settings.minYRotate)
    );

    this.controls.enableDamping = this.settings.rotationDamping;
    this.controls.enablePan = this.settings.enablePan;
    this.controls.dampingFactor = this.settings.dampingFactor;
    this.controls.autoRotate = this.settings.autoRotate;
    this.controls.autoRotateSpeed = this.settings.autoRotateSpeed;
  }

  /**
   * Animate
   *
   * This function runs on every frame
   * Its vital to Threejs and is needed
   * to run
   */
  _animate() {
    this.controls.update();
    if (this.renderer) {
      if (this._resizeRendererToDisplaySize(this.renderer)) {
        this.camera.aspect =
          this.renderer.domElement.clientWidth /
          this.renderer.domElement.clientHeight;
        this.camera.updateProjectionMatrix();
      }

      if (this.model != null && this.loaded === false) {
        this._initialRotation();
        this.uiDragNoticeDom.classList.add("start");
      }

      this.renderer.render(this.scene, this.camera);

      requestAnimationFrame(this._animate.bind(this));
    }
  }

  /**
   * Render to Display Size
   *
   * This keeps both the scene
   * and camera in proportion
   * when the parent container resizes
   *
   */
  _resizeRendererToDisplaySize(renderer) {
    const canvas = renderer.domElement;
    var width = this.uiParentDom.clientWidth;
    var height = this.uiParentDom.clientHeight;
    var canvasPixelWidth = canvas.width / window.devicePixelRatio;
    var canvasPixelHeight = canvas.height / window.devicePixelRatio;

    const needResize =
      canvasPixelWidth !== width || canvasPixelHeight !== height;
    if (needResize) {
      renderer.setSize(width, height, false);
    }
    return needResize;
  }

  /**
   * Load model
   *
   * Use GLTF Loader to load the .glb file
   * and add it to the scene
   */

  _loadModel(index) {
    const loader = new GLTFLoader();
    const modelPath = this.settings.models[index].file;
    this.currentModelUrl = modelPath;

    if (this.settings.models[index].sections) {
      /**
       * We check if the array is a string
       * This is for the benefit of the WordPress plugin
       * as the PHP passes an array as a string type
       * "[section, legs, cushion]"
       *
       * stringArraytoArray is from ./helpers
       */
      const sections =
        typeof this.settings.models[index].sections === "string" ?
        stringArraytoArray(this.settings.models[index].sections) :
        this.settings.models[index].sections;

      /**
       * Add the sections to the Section Store
       * This keeps track of which materials
       * are currently on each section, as well
       * as any other potential information
       */
      sections.forEach((section, i) => {
        let isCurrentlyInStore = false;

        // Check if the section is already in the store
        this.sectionStore.forEach(entry => {
          if (entry.id === section) {
            isCurrentlyInStore = true;
          }
        });
        // If its not, build an entry and add it to the Section Store
        if (!isCurrentlyInStore) {
          let payload = {};
          payload.id = section;
          payload.mtl = getMaterial({
            color: this.settings.defaultColor
          });
          payload.currentMtl = null;
          this.sectionStore.push(payload);
        }
      });
    }

    /**
     * Use the GLTFLoader to load the file into the scene
     *
     */
    loader.load(
      modelPath,
      gltf => {
        this.model = gltf.scene;

        /**
         * Traverse the mesh in the file
         *
         * - Enabled shadows on all mesh
         *   objects inside the GLTF file
         *
         * - Add the initial materials to the mesh
         *   Using the Section Store lets us hot
         *  load different products with the same
         *  section and keep the color choices.
         *
         * This can be disabled in the settings
         */

        this.model.traverse(o => {
          if (o.isMesh) {
            o.material.color.setHex(0xffffff);
            //  console.log(o.name, o.material);

            // Shadows
            if (this.settings.enabledShadows) {
              o.castShadow = true;
              o.receiveShadow = true;
            }
            // Initial colors

            let storeEntry = this.sectionStore.filter(x =>
              o.name.includes(x.id)
            );
            if (storeEntry.length > 0) {
              o.material = storeEntry[0].currentMtl ?
                storeEntry[0].currentMtl :
                storeEntry[0].mtl;
              o.uid = storeEntry[0].id;
            }
          }
        });

        /**
         * Set the models scale
         * Default is 1, and the optimal
         * scale of the model is 1 unit/meter
         * high which is set inside the
         * modelling software.
         */

        this.model.scale.set(
          this.settings.scale, // x
          this.settings.scale, // y
          this.settings.scale //  z
        );

        this.model.position.y = this.settings.offsetHeight;

        // SET INITIAL COLORS

        this._setInitialColor("tires", 0);
        this._setInitialColor("glass", 1);
        this._setInitialColor("lights", 1);
        this._setInitialColor("gray", 1);
        this._setInitialColor("interior", 1);

        // Add the model to the scene
        this.scene.add(this.model);

        // Remove the loader
        document
          .querySelector(".m3d-loader")
          .classList.add("--hide", "--behind-controls");
      },
      undefined,
      error => {
        console.error(error);
      }
    );

    /**
     * Flash Material
     * We're also going to create the flash color material
     * and set it to use later in _flashColor
     */

    this.flashMaterial = getMaterial({
      color: this.settings.clickFlashColor
    });
  }

  /**
   * Fill Section Selector
   *
   * Fills the uiSectionSelector with the sections for this model
   *
   * We also use activeSections.
   * This is used because when there is more than one model,
   * one of the models may have an extra section that
   * another does not. the sectionStore holds all
   * possible sections across multiple models.
   */

  _fillSectionSelector(index) {
    let sections =
      typeof this.settings.models[index].sections === "string" ?
      stringArraytoArray(this.settings.models[index].sections) :
      this.settings.models[index].sections;

    this.activeSections = sections;
    this.activeOption = this.activeSections[0];
    this.uiSectionTitle.innerText = this.activeOption.replace("-", " ");

    let container = document.querySelector(".m3d-model-sections__list");

    container.innerHTML = "";

    for (const section of sections) {
      let el = document.createElement("li");
      el.classList.add("m3d-model-sections__item");
      el.innerText = section;
      el.setAttribute("data-option", section);
      container.appendChild(el);
    }
  }

  /**
   * Build the swatches
   * There is a great default option,
   * however if the uiTraySlide changes
   * the app will still fill the new container,
   * however user experience is deferred to the
   * developer
   *
   */

  _buildSwatches(colors) {
    // Build the silder tray, or check for custom swatch parent

    if (!document.querySelector(this.settings.uiSwatchParent)) {
      this.uiSwatchParentDom = stringToNode(
        this.settings.uiSwatchParent,
        ".m3d-tray-slide"
      );
    } else {
      this.uiSwatchParentDom = document.querySelector(
        this.settings.uiSwatchParent
      );
    }

    if (this.uiSwatchParentDom.classList.contains("m3d-tray-slide")) {
      let container = document.createElement("div");
      container.className = "m3d-tray-container";
      container.appendChild(this.uiSwatchParentDom);
      this.uiParentDom.appendChild(container);

      // Initialize the slider
      slide(container, this.uiSwatchParentDom);
    } else {
      document.body.appendChild(this.uiSwatchParentDom);
    }

    /**
     * Fill the swatch parent
     */

    colors.forEach((color, i) => {
      let swatch = document.createElement("div");
      swatch.classList.add("m3d-swatch");

      // If its a texture, set the swatch background

      if (color.texture) {
        swatch.style.backgroundImage = "url(" + color.texture + ")";
      } else {
        swatch.style.background = "#" + color.color;
      }

      swatch.setAttribute("data-key", i);

      /**
       * If it doesnt exist create it and append to body,
       * If its the m3d-tray-slide (default), wrap it in a container
       * and send it to the uiParentDom as default
       *  */

      this.uiSwatchParentDom.append(swatch);
    });
  }

  /**
   * Add event listeners
   *
   * For all the UI elements
   */
  _addEventListeners() {
    /**
     * Previous and next buttons
     */

    this.uiSectionPrev.addEventListener("click", e => this._selectSection(e));
    this.uiSectionNext.addEventListener("click", e => this._selectSection(e));

    /**
     * Select a swatch
     */
    this.swatches = document.querySelectorAll(".m3d-swatch");

    for (const swatch of this.swatches) {
      /**
       * We seperate the click handlers here
       * to down and up, so that we can determine
       * whether the user has dragged the mouse/finger
       * or not. Note that we pass the desired function
       * as a callback in _onMouseUp
       */

      // Desktop

      swatch.addEventListener(
        "mousedown",
        e => this._onMouseDown(e, "desktop"),
        false
      );

      swatch.addEventListener(
        "mouseup",
        e => this._onMouseUp(e, () => this._selectSwatch(e), "desktop"),
        false
      );

      // Mobile

      swatch.addEventListener(
        "touchstart",
        e => this._onMouseDown(e, "mobile"),
        false
      );

      swatch.addEventListener(
        "touchend",
        e => this._onMouseUp(e, () => this._selectSwatch(e), "mobile"),
        false
      );
    }

    /**
     * Raycasting eventListeners
     */

    // Desktop

    this.renderer.domElement.addEventListener(
      "mousedown",
      e => this._onMouseDown(e, "desktop"),
      false
    );

    this.renderer.domElement.addEventListener(
      "mouseup",
      e =>
      this._onMouseUp(
        e,
        type => {}, //this._raycast(type),
        "desktop"
      ),
      false
    );

    // Mobile

    this.renderer.domElement.addEventListener(
      "touchstart",
      e => this._onMouseDown(e, "mobile"),
      false
    );

    this.renderer.domElement.addEventListener(
      "touchend",
      e =>
      this._onMouseUp(
        e,
        type => {}, //this._raycast(type),
        "mobile"
      ),
      false
    );
  }

  /**
   * These two functions track
   * the mouse position on down
   * and then up, compares them
   * and if they're the same,
   * runs a callback (complete());
   *
   * This is used to determine whether
   * the user was dragging or clicking
   */

  _onMouseDown(event, type) {
    this.userMouseClick.down.x =
      type === "desktop" ?
      round(event.clientX, 2) :
      round(event.changedTouches[0].clientX, 2);
    this.userMouseClick.down.y =
      type === "desktop" ?
      round(event.clientY, 2) :
      round(event.changedTouches[0].clientY, 2);
  }

  _onMouseUp(event, complete, type) {
    let {
      down,
      up
    } = this.userMouseClick;

    this.userMouseClick.up.x =
      type === "desktop" ?
      round(event.clientX, 2) :
      round(event.changedTouches[0].clientX, 2);
    this.userMouseClick.up.y =
      type === "desktop" ?
      round(event.clientY, 2) :
      round(event.changedTouches[0].clientY, 2);

    // If the mouse position is equal, complete the listener
    if (up.x === down.x && up.y === down.y) {
      complete(type);
    }
  }

  // Function - Raycast which object to click, flash red on click
  _raycast(type) {
    let {
      up
    } = this.userMouseClick;

    let normal = {};

    // Normalize the click location

    let rect = this.renderer.domElement.getBoundingClientRect();

    normal.x =
      type === "desktop" ?
      ((event.clientX - rect.left) / this.renderer.domElement.clientWidth) *
      2 -
      1 :
      ((event.changedTouches[0].clientX - rect.left) /
        this.renderer.domElement.clientWidth) *
      2 -
      1;

    normal.y =
      type === "desktop" ?
      -(
        (event.clientY - rect.top) /
        this.renderer.domElement.clientHeight
      ) *
      2 +
      1 :
      -(
        (event.changedTouches[0].clientY - rect.top) /
        this.renderer.domElement.clientHeight
      ) *
      2 +
      1;

    // update the picking ray with the camera and mouse position
    this.raycaster.setFromCamera(normal, this.camera);

    // calculate objects intersecting the picking ray
    var intersects = this.raycaster.intersectObjects(this.scene.children, true);

    if (intersects[0]) {
      var object = intersects[0].object;
    }

    if (object && object.uid) {
      // Accept the raycast
      if (object.uid != this.activeOption) {
        this.activeOption = object.uid;
        this.uiSectionTitle.innerText = this.activeOption.replace("-", " ");
        this._filterSwatches(this.activeOption);
      }

      this._flashColor();
    }
  }

  /**
   * Select Section
   *
   * This is used for both the Section Selector
   * and raycasting
   *
   */

  _selectSection(e) {
    let option = e.target;
    let currentIndex = this.activeSections.indexOf(this.activeOption);
    if (option.dataset.option == "prev") {
      // Loop around
      if (currentIndex > 0) {
        this.activeOption = this.activeSections[currentIndex - 1];
      } else {
        this.activeOption = this.activeSections[this.activeSections.length - 1];
      }
    } else if (option.dataset.option == "next") {
      // Loop around
      if (currentIndex < this.activeSections.length - 1) {
        this.activeOption = this.activeSections[currentIndex + 1];
      } else {
        this.activeOption = this.activeSections[0];
      }
    } else {
      // Select from list
      this.activeOption = option.dataset.option;
    }
    this.uiSectionTitle.innerText = this.activeOption.replace("-", " ");
    this._flashColor();
    this._filterSwatches(this.activeOption);
  }

  /**
   * Select Swatch
   * This handles settings the color oto the
   * active section
   *
   */

  _selectSwatch(e) {
    let colorData;
    colorData = this.settings.colors[parseInt(e.target.dataset.key)];
    let material = getMaterial(colorData);

    this._setMaterial(this.model, this.activeOption, material);
  }

  _setMaterial(parent, type, mtl) {
    parent.traverse(o => {
      if (o.isMesh && o.uid != null) {
        if (o.uid == type) {
          o.material = mtl;
          for (let section of this.sectionStore) {
            if (section.id == type) {
              section.currentMtl = mtl;
            }
          }
        }
      }
    });
  }

  updateColor(hex, metallic, shine, roughness) {
    let rough = parseInt(roughness) / 100;

    let material = getMaterial({
      color: hex,
      sections: ["body"],
      metallic: metallic,
      roughness: rough,
      shininess: shine
    });

    this._setMaterial(this.model, "body", material);
  }

  /**
   * Filter swatches
   *
   * This hides swatches that aren't avaiable for a chosen section
   */

  _filterSwatches(option) {
    let availableColors = [];

    for (let [i, color] of this.settings.colors.entries()) {
      if (color.sections.includes(option)) {
        availableColors.push(i);
      }
    }
    for (const swatch of this.swatches) {
      let position = swatch.dataset.key;
      swatch.classList.add("--hide");
      if (availableColors.includes(parseInt(position))) {
        swatch.classList.remove("--hide");
      }
    }
  }

  /**
   * This color flashes the section when its selected
   * Used in the Select Section method
   *
   */

  _flashColor(parent = this.model) {
    parent.traverse(o => {
      if (o.isMesh && o.uid != null) {
        if (o.uid === this.activeOption) {
          // Set a placeholder color and flash
          if (o.material != this.flashMaterial) {
            o.currentMaterial = o.material;
            o.material = this.flashMaterial;
          }
          setTimeout(function () {
            // Revert back to previous material
            o.material = o.currentMaterial;
          }, 100);
        }
      }
    });
  }

  /**
   * The initial rotation of the model
   */

  _initialRotation() {
    this.initRotate++;
    if (this.initRotate <= 120) {
      this.model.rotation.y += Math.PI / 60;
    } else {
      this.loaded = true;
    }
  }

  /**
   * Public Methods
   */

  downloadImage() {
    let dataURL = this.renderer.domElement.toDataURL("image/png");
    var a = document.createElement("a");
    a.href = dataURL;
    a.download = `custom-cybertruck.png`;

    a.click();
  }

  _setInitialColor(section, idx) {
    let material = getMaterial(this.settings.colors[idx]);
    this._setMaterial(this.model, section, material);
  }

  /**
   * END
   */
}

/**
 * Enable for demo / testing
 */

let model = {
  name: "Cybertruck",
  file: "./public/models/cybertruck_7.glb",
  sections: ["body", "tires", "interior", "glass", "gray", "lights"]
};

let merch = new Merch3d({
  models: [model],
  shadowResolution: 4096,
  cameraDistance: 5
});

merch.run();

/**
 * Color picker
 *
 */

let masterColor = 'rgb(241, 241, 241)';

let shineRange = document.querySelector("#shineRange");
let metCheck = document.querySelector("#metCheck");
let metRange = document.querySelector("#metRange");
let download = document.querySelector("#download");

//

let picker = AColorPicker.from(".picker").on("change", (picker, color) => {
  masterColor = color;
  updateTheCybertruck(color);
});

shineRange.addEventListener('input', () => {
  updateTheCybertruck(masterColor);
});

metCheck.addEventListener('input', () => {
  updateTheCybertruck(masterColor);
});

metRange.addEventListener('input', () => {
  updateTheCybertruck(masterColor);
});

//

download.addEventListener('click', () => merch.downloadImage());



function updateTheCybertruck(color) {
  let metallic = metCheck.checked;
  let shine = shineRange.value;
  let roughness = metRange.value;
  merch.updateColor(color, metallic, shine, roughness);
}

let toggle = document.querySelector('.controls__mobile-button');

let controls = document.querySelector('.scroll');
toggle.addEventListener('click', () => {
  
  controls.classList.toggle('show');
  toggle.classList.toggle('show');
});


export default Merch3d;